import { useEffect, useState } from 'react';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import {
  capitalizeFirstLetter,
  extractProductName,
  prepareProductsUrl,
} from '../lib/util';
import 'src/common/style/importantStyle.scss';
import 'src/common/style/common.scss';
import { swapCountryAndLanguage } from 'src/common/utils/swapCountryAndLanguage';
import { useLang } from 'src/common/hooks/widgets/useLang';
import countryList from '../services/currencies.json';
import NoteDrawer from './NoteDrawer/NoteDrawer';
import { getParamsFromUrl } from 'src/common/utils/getParamsFromUrl';
import { OMNICHANNEL_HEADER } from '../constants/default';
import { useOmniChannelSelector } from '../lib/redux/hooks';
import { createUseTranslation } from 'src/common/hooks/widgets/useTranslation';
import messages from '../locales/messages';

export interface OmnichannelProps {
  langCode: string;
  countryToTest: string;
  test: boolean;
  clickHandlerMockup?: boolean;
  editionIdMockup?: string;
  offeringMockup?: string;
  marketplaceHrefMockup?: string;
  hostUrlMockup?: string;
}

export interface PromotionText {
  [locale: string]: string;
}

export interface Promotion {
  startTs: string; // ISO 8601 date string
  endTs: string; // ISO 8601 date string
  text: PromotionText;
  cta: string;
  status: boolean;
}

export interface Offering {
  [offeringId: string]: OfferingDetails;
}

export interface OfferingDetails {
  name: string;
  marketplace: boolean;
  ecomm?: string[];
  aws?: string;
  azure?: string;
  cas: boolean;
}

export interface ProductInterface {
  id: string;
  active: boolean;
  rootNodeName: string;
  productTitle?: string;
  productName: string;
  pageTitle: string;
  location: string;
  object: string;
  pageUrl: string;
  awsUrl?: string;
  azureUrl?: string;
  isConsultSeller?: boolean;
  isIbmMarket?: boolean;
  promotions?: Promotion[];
  offering: Offering;
}

const Omnichannel = ({
  langCode,
  countryToTest,
  test,
  clickHandlerMockup = false,
  editionIdMockup = '',
  offeringMockup = '',
  marketplaceHrefMockup = '',
  hostUrlMockup = 'dummy-value',
}: OmnichannelProps) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);

  const [environment] = useEnv();

  const [, setCurrency] = useState('');
  const [, setCountry] = useState('');

  const AEMMessages = useOmniChannelSelector(
    (state) => state.translations.messages,
  );
  const useTranslation = createUseTranslation(messages, AEMMessages);

  // lang and userlocale is based out of ddo.
  // lang and userlocale variable will be use once we start introduce multi-language.
  const [lang, userLocale, ddoCountry] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
  );

  window.urxEnvironment = environment;

  // Setting the state here to pass it on the widget component.
  const [omnichannel, setOmniChannel] = useState(
    test ? clickHandlerMockup : false,
  );
  const [marketplaceHref, setMarketplaceHref] = useState(
    test ? marketplaceHrefMockup : '',
  );
  const [editionId, setEditionId] = useState(test ? editionIdMockup : '');
  const [productTitle, setProductTitle] = useState('');
  const [product, setProduct] = useState<ProductInterface>();

  const attachEventListener = (
    rootNodeName: string,
    setOmniChannel: React.Dispatch<React.SetStateAction<boolean>>,
    setMarketplaceHref: React.Dispatch<React.SetStateAction<string>>,
    setEditionId: React.Dispatch<React.SetStateAction<string>>,
  ) => {
    const webComponents = document.querySelectorAll(rootNodeName);
    webComponents.forEach((webComponent) => {
      if (webComponent) {
        const button = webComponent.querySelector(
          'button[data-button="omnichannel"]',
        );
        if (button) {
          button.addEventListener('click', () =>
            omniChannelClickHandler(
              button,
              setOmniChannel,
              setMarketplaceHref,
              setEditionId,
            ),
          );
        }
      }
    });
  };

  /* customised listerner only for aspera,
   * till dynamic pricing table is aligned with omnichannel buy now button
   */
  const attachEventListenerForAspera = (
    setOmniChannel: React.Dispatch<React.SetStateAction<boolean>>,
    setMarketplaceHref: React.Dispatch<React.SetStateAction<string>>,
    setEditionId: React.Dispatch<React.SetStateAction<string>>,
  ) => {
    const webComponents = document.querySelectorAll(
      'c4d-pricing-table-header-cell-cta',
    );
    webComponents.forEach((webComponent) => {
      // Added console for investigation only
      console.log(`webComponents found ${webComponent}`);

      if (webComponent.shadowRoot) {
        const button = webComponent.shadowRoot.querySelector('a');

        if (button) {
          button.addEventListener('click', (event) => {
            event.preventDefault(); // to stop default redirection

            // Setting the state here to pass it on the widget component.
            setOmniChannel(true);

            // marketplaceHref is to fetch the ibm.com mktplace deeplink
            const markeplaceUrl = button.getAttribute('href');
            setMarketplaceHref(markeplaceUrl || '');

            // EditionId, is to track for instrumentation
            const editionId = markeplaceUrl?.split('editionID=')[1];
            setEditionId(editionId || '');

            console.log(`opening OC-slider for ${editionId}`);
          });
        }
      }
    });
  };

  const omniChannelClickHandler = (
    button: Element,
    setOmniChannel: React.Dispatch<React.SetStateAction<boolean>>,
    setMarketplaceHref: React.Dispatch<React.SetStateAction<string>>,
    setEditionId: React.Dispatch<React.SetStateAction<string>>,
  ) => {
    // Setting the state here to pass it on the widget component.
    setOmniChannel(true);

    // marketplaceHref is to fetch the ibm.com mktplace deeplink
    const spanIdElementHref = button.querySelector('span[data-href]');
    setMarketplaceHref(spanIdElementHref?.getAttribute('data-href') || '');

    // EditionId, is to track for instrumentation
    const spanIdEditionId = button.querySelector('span[data-widget-editionid]');
    setEditionId(spanIdEditionId?.getAttribute('data-widget-editionid') || '');
  };

  const initOCConfig = (productTitleFetched: string) => {
    fetch(
      `https://ace-project-bucket.s3.us-east.cloud-object-storage.appdomain.cloud/${
        window.location.host === 'www.ibm.com' ? 'production' : 'test'
      }/omnichannel.json`,
      {
        method: 'GET',
        headers: OMNICHANNEL_HEADER,
      },
    )
      .then((res) => res.json())
      .then((data) =>
        setProduct(
          data.find(
            (product: ProductInterface) =>
              product?.active &&
              product?.productTitle
                ?.toLowerCase()
                .match(productTitleFetched.toLowerCase()),
          ),
        ),
      )
      .catch((error) => {
        console.error(
          'error while fetching omnichannel products via ace cos bucket',
          error,
        );
      });
  };

  useEffect(() => {
    // fetching the productTitle via page url or query param - pname
    const productTitleFetched =
      getParamsFromUrl('pname') ||
      extractProductName(test ? hostUrlMockup : window.location.href) ||
      '-';

    // setting the product title either via window.location.href or via param pname
    setProductTitle(capitalizeFirstLetter(productTitleFetched));

    console.log(`Product Title Fetch - ${productTitleFetched}`);

    // intializing the OC component with fetched productTitle
    initOCConfig(productTitleFetched);
  }, []);

  useEffect(() => {
    if (product?.active) {
      console.log(`Products matched - ${product.pageTitle}`);
      if (product.pageTitle === 'aspera') {
        setTimeout(
          async () =>
            attachEventListenerForAspera(
              setOmniChannel,
              setMarketplaceHref,
              setEditionId,
            ),
          1000,
        );
      } else {
        // adding an event to the button to call the omnichannel component
        attachEventListener(
          product.rootNodeName,
          setOmniChannel,
          setMarketplaceHref,
          setEditionId,
        );
      }
    }
  }, [product]);

  return (
    <div className="omnichannel-panel widget-styles">
      {product && (
        <NoteDrawer
          id={`${productTitle}-omnichannel`}
          omniChannel={omnichannel}
          setOmniChannel={setOmniChannel}
          ibmMarketPlaceHref={marketplaceHref}
          editionId={editionId}
          headerTitle={productTitle}
          product={product}
          localeCode={localeCode}
          useTranslation={useTranslation}
          ddoCountry={ddoCountry ? ddoCountry : 'us'}
        />
      )}
    </div>
  );
};

export default Omnichannel;
